/**
 * Web equivalent of the mobile TransactionDocumentUploadModal.
 *
 * Step 2 of the Buying+Active CREATE-LAST flow. The agent picks offer
 * documents (RPA first, then counter offers in order), confirms the set is
 * complete via "No more documents", then submits. The batch is sent to the
 * stateless parse endpoint and the reconciled { timeline, parties,
 * propertyAddress } is handed back to the parent via onParsed().
 *
 * Nothing is persisted on the backend during this step — no transaction id
 * exists yet. The parse endpoint is POST /api/v1/ria/transactions/parse-documents.
 */

import React, { useState, useRef } from 'react';
import { auth } from '../../firebase/config';
import { parseTransactionDocuments } from '../../services/ria.service';

const LABEL_OPTIONS = [
  { value: 'RPA', label: 'Purchase Agreement' },
  { value: 'COUNTER_OFFER', label: 'Counter Offer' },
];

const formatBytes = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const TransactionDocumentUploadModal = ({ visible, onClose, onParsed }) => {
  const [files, setFiles] = useState([]);
  const [noMoreDocuments, setNoMoreDocuments] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [parseError, setParseError] = useState('');
  const fileInputRef = useRef(null);

  const hasRpa = files.some(f => f.label === 'RPA');
  const rpaFirst = files.length === 0 || files[0].label === 'RPA';
  const canSubmit = noMoreDocuments && files.length > 0 && hasRpa && rpaFirst && !isSubmitting;

  const handleAddDocument = () => {
    if (isSubmitting) return;
    setParseError('');
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    const newFile = {
      name: picked.name,
      size: picked.size,
      mimeType: picked.type || 'application/pdf',
      label: files.length === 0 ? 'RPA' : 'COUNTER_OFFER',
      fileObj: picked,
    };
    setFiles(prev => [...prev, newFile]);
    // Reset the input so the same file can be picked again if needed
    e.target.value = '';
  };

  const moveFile = (index, direction) => {
    const next = index + direction;
    if (next < 0 || next >= files.length) return;
    setFiles(prev => {
      const copy = [...prev];
      [copy[index], copy[next]] = [copy[next], copy[index]];
      return copy;
    });
  };

  const setLabel = (index, label) => {
    setFiles(prev => prev.map((f, i) => i === index ? { ...f, label } : f));
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setParseError('');
    try {
      const currentUser = auth.currentUser;
      const firebaseToken = currentUser ? await currentUser.getIdToken(true) : null;

      const parts = files.map(f => ({ blob: f.fileObj, name: f.name, label: f.label }));
      const result = await parseTransactionDocuments('BUYING', parts, { firebaseToken });

      onParsed(result, files);
      // Reset internal state for any subsequent re-open
      setFiles([]);
      setNoMoreDocuments(false);
    } catch (err) {
      const msg = err?.response?.data?.message
        || err?.response?.data?.error
        || err?.message
        || 'Document parsing failed. Please try again.';
      setParseError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  if (!visible) return null;

  return (
    <div className="tdu-overlay" onClick={handleClose}>
      <div className="tdu-container" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="tdu-header">
          <span className="tdu-title">Upload offer documents</span>
          <button className="tdu-close-btn" onClick={handleClose} disabled={isSubmitting}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Subtitle */}
        <p className="tdu-subtitle">
          Add documents one at a time. The Purchase Agreement must be first, then counter offers in signed order.
          Tick "No more documents" when you're done adding files.
        </p>

        {/* File list */}
        <div className="tdu-file-list">
          {files.length === 0 ? (
            <div className="tdu-empty-state">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="12" y1="11" x2="12" y2="17"/>
                <line x1="9" y1="14" x2="15" y2="14"/>
              </svg>
              <span className="tdu-empty-text">No documents yet — click "Add document" below.</span>
            </div>
          ) : (
            files.map((file, idx) => (
              <div key={`${file.name}-${idx}`} className="tdu-file-row">
                <div className="tdu-file-index">{idx + 1}</div>
                <div className="tdu-file-main">
                  <span className="tdu-file-name" title={file.name}>{file.name}</span>
                  {file.size > 0 && <span className="tdu-file-meta">{formatBytes(file.size)}</span>}
                  <div className="tdu-label-row">
                    {LABEL_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        className={`tdu-label-chip ${file.label === opt.value ? 'active' : ''}`}
                        onClick={() => setLabel(idx, opt.value)}
                        disabled={isSubmitting}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="tdu-file-actions">
                  <button
                    className="tdu-icon-btn"
                    onClick={() => moveFile(idx, -1)}
                    disabled={idx === 0 || isSubmitting}
                    title="Move up"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="18 15 12 9 6 15"/></svg>
                  </button>
                  <button
                    className="tdu-icon-btn"
                    onClick={() => moveFile(idx, 1)}
                    disabled={idx === files.length - 1 || isSubmitting}
                    title="Move down"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  <button
                    className="tdu-icon-btn tdu-icon-btn-danger"
                    onClick={() => removeFile(idx)}
                    disabled={isSubmitting}
                    title="Remove"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              </div>
            ))
          )}

          {files.length > 0 && !hasRpa && (
            <p className="tdu-warning">One file must be labeled Purchase Agreement.</p>
          )}
          {files.length > 0 && hasRpa && !rpaFirst && (
            <p className="tdu-warning">The Purchase Agreement must be first in the list.</p>
          )}
        </div>

        {/* "No more documents" checkbox */}
        <button
          className="tdu-no-more-row"
          onClick={() => {
            if (isSubmitting) return;
            if (!noMoreDocuments && files.length === 0) {
              setParseError('Please upload at least one document.');
              return;
            }
            setParseError('');
            setNoMoreDocuments(v => !v);
          }}
          disabled={isSubmitting}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={noMoreDocuments ? '#2D1B69' : '#9CA3AF'} strokeWidth="2">
            {noMoreDocuments
              ? <><rect x="3" y="3" width="18" height="18" rx="2" fill="#2D1B69" stroke="#2D1B69"/><polyline points="9 12 11 14 15 10" stroke="white" strokeWidth="2.5"/></>
              : <rect x="3" y="3" width="18" height="18" rx="2"/>
            }
          </svg>
          <span className="tdu-no-more-text">No more documents</span>
        </button>

        {/* Parsing banner */}
        {isSubmitting && (
          <div className="tdu-parsing-banner">
            <span className="tdu-parsing-spinner"/>
            <span className="tdu-parsing-text">Reconciling documents &amp; generating timeline…</span>
          </div>
        )}

        {/* Error */}
        {parseError && (
          <div className="tdu-error-banner">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>{parseError}</span>
          </div>
        )}

        {/* Footer */}
        <div className="tdu-footer">
          <button className="tdu-btn tdu-btn-secondary" onClick={handleAddDocument} disabled={isSubmitting || noMoreDocuments}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add document
          </button>
          <button className="tdu-btn tdu-btn-primary" onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting ? 'Sending…' : 'Submit'}
          </button>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>
    </div>
  );
};

export default TransactionDocumentUploadModal;
